/**
 * Armazenamento do token JWT e do usuário da sessão.
 *
 * DECISÃO (contrato + instrução): o token é guardado em `localStorage`. O
 * contrato de login devolve `{token}` no corpo JSON (não há cookie httpOnly) e
 * a ferramenta é interna, usada por vendedores em rede corporativa. O acesso ao
 * storage é centralizado aqui para permitir uma troca futura por cookie
 * httpOnly sem tocar no resto da aplicação.
 */

import type { User } from "@/types/api";

const TOKEN_KEY = "eye.token";
const USER_KEY = "eye.user";

/** Evento global emitido quando a API responde 401 (token inválido/expirado). */
export const UNAUTHORIZED_EVENT = "eye:unauthorized";

/** Devolve o token atual, ou `null` se não houver sessão. */
export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Grava o token após o login. */
export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Storage indisponível (modo privado/quota) — sessão vive só em memória.
  }
}

/** Devolve o usuário em cache, para hidratação instantânea antes do `/me`. */
export function getStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

/** Guarda o usuário da sessão em cache local. */
export function setStoredUser(user: User): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    // Ignorado — o cache é apenas uma otimização de hidratação.
  }
}

/** Limpa token e usuário (logout ou falha de autenticação). */
export function clearSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // Ignorado.
  }
}

/** Emite o evento global de não-autorizado (consumido pelo AuthProvider). */
export function notifyUnauthorized(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
  }
}
